class ProductGiftWrap extends HTMLElement {
  constructor() {
    super();

    this.miniCart = document.querySelector("mini-cart");
    this.productKey = this.dataset.productKey;
    this.productQuantity = parseInt(this.dataset.productQuantity);
    this.giftWrapId = parseInt(this.dataset.giftWrapId);
    this.giftWrapsInCart = parseInt(this.dataset.giftWrapsInCart);

    this.loadingOverlay = this.querySelector(".loading-overlay");
    this.sections = this.getSectionsToRender().map(
      (section) => section.section,
    );
    this.giftWrapCheckbox = this.querySelector('input[type="checkbox"]');
    this.totalWrappedProductsInCart = 0;
    this.totalWrappedProductsInCart += document.querySelectorAll(
      `[data-gift-wrap-id="${this.giftWrapId}"][data-gift-wrapping="true"]`,
    )?.length;

    this.addEventListeners();
    this.getProductProperties();

    if (this.miniCart) return;

    this.handleCartUpdate();
  }

  addEventListeners() {
    // When the gift-wrapping checkbox is checked or unchecked.
    this.giftWrapCheckbox.addEventListener(
      "change",
      this.handleGiftWrapChange.bind(this),
    );

    document.addEventListener("cart:updated", this.handleCartUpdate.bind(this));
    document.addEventListener(
      "cartdrawer:opened",
      this.handleCartUpdate.bind(this),
    );
  }

  handleGiftWrapChange(event) {
    event.target.checked
      ? this.setProductGiftWrap()
      : this.removeProductGiftWrap();
  }

  /**
   * If the gift wrap is no longer in the cart and the checkbox is checked, unchecks it and updates the product property.
   */
  handleCartUpdate() {
    if (!document.body.contains(this)) return;
    // Re-fetch giftWrapsInCart to ensure it's up-to-date with cart changes
    this.giftWrapsInCart = parseInt(this.dataset.giftWrapsInCart);
    if (this.giftWrapsInCart === 0 && this.giftWrapCheckbox.checked) {
      this.giftWrapCheckbox.checked = false; // Uncheck the box
      this.updateProductProperty(false);
    }
    // If we don't have the right amount of gift-wrap items in the cart.
    else if (
      this.giftWrapsInCart > 0 &&
      this.giftWrapCheckbox.checked &&
      this.totalWrappedProductsInCart > this.giftWrapsInCart
    ) {
      this.setProductGiftWrap();
    }
  }

  /**
   * Sets the product gift wrap by adding a gift wrap to the cart.
   * Displays a loading overlay during the operation.
   */
  setProductGiftWrap() {
    this.loadingOverlay.classList.remove("hidden");

    const body = {
      updates: {
        [this.giftWrapId]: this.giftWrapsInCart + 1,
      },
      sections: this.sections,
      sections_url: window.location.pathname,
    };

    this._fetchCart(theme.routes.cart_update_url, body)
      .then(() => {
        this.updateProductProperty(true);
      })
      .catch((e) => {
        console.error("Error setting gift wrap:", e);
      });
  }

  /**
   * Removes the product gift wrap by decreasing the gift wrap quantity in the cart.
   * Displays a loading overlay during the operation.
   */
  removeProductGiftWrap() {
    this.loadingOverlay.classList.remove("hidden");

    const body = {
      updates: {
        [this.giftWrapId]: Math.max(0, this.giftWrapsInCart - 1),
      },
      sections: this.sections,
      sections_url: window.location.pathname,
    };

    this._fetchCart(theme.routes.cart_update_url, body)
      .then(() => {
        this.updateProductProperty(false);
      })
      .catch((e) => {
        console.error("Error removing gift wrap:", e);
      });
  }

  /**
   * Updates a product property in the cart.
   * @param {boolean} propertyValue - The value to set for the _has_gift_wrap property.
   */
  updateProductProperty(propertyValue) {
    this.productProperties._has_gift_wrap = propertyValue;
    const formData = {
      id: this.productKey,
      properties: this.productProperties,
      quantity: this.productQuantity,
      sections: this.sections,
      sections_url: window.location.pathname,
    };

    this._fetchCart(theme.routes.cart_change_url, formData)
      .then((response) => {
        this.loadingOverlay.classList.add("hidden");

        if (document.body.classList.contains("template-cart")) {
          window.location.href = theme.routes.cart_url;
          return;
        }

        this.miniCart && this.miniCart.renderContents(response);
      })
      .catch((e) => {
        console.error("Error updating product property:", e);
      });
  }

  getProductProperties() {
    this.productProperties =
      this.productProperties ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent);
    delete this.productProperties._has_gift_wrap;
    return this.productProperties;
  }

  /**
   * Helper method to handle fetch requests to the cart API.
   * @param {string} url - The URL for the fetch request.
   * @param {object} data - The data to be sent in the request body.
   * @returns {Promise<object>} - A promise that resolves with the JSON response.
   * @private
   */
  _fetchCart(url, data) {
    return fetch(url, {
      ...fetchConfig(),
      ...{ body: JSON.stringify(data) },
    }).then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(
            error.message || "Something went wrong with the cart API.",
          );
        });
      }
      return response.json();
    });
  }

  /**
   * Returns an array of sections to be rendered.
   * @returns {Array<object>} An array of section objects.
   */
  getSectionsToRender() {
    const SHOPIFY_SECTION_SELECTOR = ".shopify-section";
    return [
      {
        id: "mini-cart",
        section: "mini-cart",
        selector: SHOPIFY_SECTION_SELECTOR,
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: SHOPIFY_SECTION_SELECTOR,
      },
      {
        id: "mobile-cart-icon-bubble",
        section: "mobile-cart-icon-bubble",
        selector: SHOPIFY_SECTION_SELECTOR,
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: SHOPIFY_SECTION_SELECTOR,
      },
    ];
  }
}

customElements.define("product-gift-wrap", ProductGiftWrap);
